<template>
  <div :class="ns.b()">
    <div :class="ns.e('left')">
      <template v-if="reactData.editStr === ''">
        <z-button
          v-if="showNewBtn"
          :icon="toolbarIcons.CirclePlusFilled"
          type="primary"
          @click="$grid.handleInsert?.()"
        >
          {{ t('z.table.toolbar.add') }}
        </z-button>
        <z-button
          v-if="showCopyBtn"
          :icon="toolbarIcons.CopyDocument"
          type="primary"
          @click="$grid.handleCopy?.()"
        >
          {{ t('z.table.toolbar.copy') }}
        </z-button>
        <z-button
          v-if="showDelBtn"
          :icon="toolbarIcons.Delete"
          type="warning"
          @click="$grid.handleDeleteRows?.()"
        >
          {{ t('z.table.toolbar.delete') }}
        </z-button>
        <template v-for="btn in customBtns" :key="btn.label">
          <z-button :icon="btn.icon" :type="btn.type" @click="btn.onClick?.()">
            {{ btn.label }}
          </z-button>
        </template>
      </template>
      <template v-else-if="tableEditable">
        <z-button
          :icon="toolbarIcons.Finished"
          type="primary"
          @click="$grid.saveData?.()"
        >
          {{ t('z.table.toolbar.save') }}
        </z-button>
        <z-button
          :icon="toolbarIcons.Finished"
          type="warning"
          @click="$grid.queryByExamplePage()"
        >
          {{ t('z.table.toolbar.cancel') }}
        </z-button>
        <z-button
          :icon="toolbarIcons.Finished"
          type="primary"
          @click="$grid.saveDataAndNew?.()"
        >
          {{ t('z.table.toolbar.saveAndNew') }}
        </z-button>
        <z-button
          :icon="toolbarIcons.CopyDocument"
          type="primary"
          @click="$grid.saveDataAndCopy?.()"
        >
          {{ t('z.table.toolbar.saveAndCopy') }}
        </z-button>
      </template>
    </div>
    <div :class="ns.e('right')">
      <z-tooltip :content="t('z.table.toolbar.refresh')" placement="top">
        <z-button
          link
          :icon="toolbarIcons.Refresh"
          :aria-label="t('z.table.toolbar.refresh')"
          @click="$grid.queryByExamplePage()"
        />
      </z-tooltip>
      <z-tooltip :content="t('z.table.toolbar.clearSort')" placement="top">
        <z-button
          link
          :icon="toolbarIcons.Sort"
          :aria-label="t('z.table.toolbar.clearSort')"
          @click="$grid.clearAllSort()"
        />
      </z-tooltip>
      <z-tooltip :content="t('z.table.toolbar.filter')" placement="top">
        <z-button
          link
          :icon="toolbarIcons.Filter"
          :class="[ns.e('filter-btn'), { [ns.is('active')]: hasActiveFilter }]"
          :aria-label="t('z.table.toolbar.filter')"
          @click="reactData.filterVisible = true"
        />
      </z-tooltip>
      <z-tooltip
        v-if="showColumnsPanel"
        :content="t('z.table.toolbar.custom')"
        placement="top"
      >
        <z-button
          link
          :icon="toolbarIcons.Operation"
          :class="[
            ns.e('columns-btn'),
            { [ns.is('active')]: reactData.customStore.open },
          ]"
          :aria-label="t('z.table.toolbar.custom')"
          @click="$grid.toggleColumnsPanel?.()"
        />
      </z-tooltip>
      <z-tooltip
        v-if="showAiAssistant"
        :content="t('z.table.aiAssistant.title')"
        placement="top"
      >
        <z-button
          link
          :icon="toolbarIcons.MagicStick"
          :class="[
            ns.e('ai-btn'),
            { [ns.is('active')]: reactData.aiAssistantStore.visible },
          ]"
          :aria-label="t('z.table.aiAssistant.title')"
          @click="$grid.toggleAiAssistant?.()"
        />
      </z-tooltip>
      <div
        :class="[
          ns.e('search'),
          { [ns.is('active')]: Boolean(reactData.searchKeyword.trim()) },
        ]"
      >
        <z-input
          v-model="reactData.searchKeyword"
          size="small"
          clearable
          :prefix-icon="toolbarIcons.Search"
          :placeholder="t('z.table.toolbar.searchPlaceholder')"
          :class="ns.e('search-input')"
          :aria-label="t('z.table.toolbar.search')"
          @input="handleGlobalSearchInput"
          @keyup.enter="handleGlobalSearch"
          @clear="handleGlobalSearch"
        />
      </div>
      <z-dropdown trigger="click" @command="handleMoreCommand">
        <z-tooltip :content="t('z.table.toolbar.more')" placement="top">
          <z-button
            link
            :icon="toolbarIcons.ArrowDown"
            :aria-label="t('z.table.toolbar.more')"
          />
        </z-tooltip>
        <template #dropdown>
          <z-dropdown-menu>
            <z-dropdown-item command="export">{{
              t('z.table.toolbar.export')
            }}</z-dropdown-item>
            <z-dropdown-item v-if="!showColumnsPanel" command="custom">
              {{ t('z.table.toolbar.custom') }}
            </z-dropdown-item>
          </z-dropdown-menu>
        </template>
      </z-dropdown>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount } from 'vue'
import { debounce } from 'lodash-es'
import ZButton from '@zoryn/components/button'
import ZDropdown, {
  ZDropdownItem,
  ZDropdownMenu,
} from '@zoryn/components/dropdown'
import ZInput from '@zoryn/components/input'
import ZTooltip from '@zoryn/components/tooltip'
import {
  Filter,
  CirclePlusFilled,
  CopyDocument,
  Delete,
  Refresh,
  Finished,
  ArrowDown,
  Operation,
  Search,
  Sort,
  MagicStick,
} from '@zoryn/icons'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { resolveIconComponent } from '@zoryn/utils'
import { hasActiveFilterGroups } from '../filter/filter-group'
import { resolveGridEditable } from '../column/meta'
import { isSideBarEnabled } from '../utils/side-bar'
import { isAiAssistantEnabled } from '../methods/ai-assistant'
import {
  GLOBAL_SEARCH_DEBOUNCE_MS,
  GLOBAL_SEARCH_MIN_LENGTH,
} from '../utils/global-search'
import type { DataGrid } from '../types'

const toolbarIcons = {
  CirclePlusFilled: resolveIconComponent(CirclePlusFilled)!,
  CopyDocument: resolveIconComponent(CopyDocument)!,
  Delete: resolveIconComponent(Delete)!,
  Refresh: resolveIconComponent(Refresh)!,
  Finished: resolveIconComponent(Finished)!,
  Filter: resolveIconComponent(Filter)!,
  Operation: resolveIconComponent(Operation)!,
  Search: resolveIconComponent(Search)!,
  ArrowDown: resolveIconComponent(ArrowDown)!,
  Sort: resolveIconComponent(Sort)!,
  MagicStick: resolveIconComponent(MagicStick)!,
}

const ns = useNamespace('grid-toolbar')
const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const { reactData, options } = $grid

const tableEditable = computed(() => resolveGridEditable($grid.props, options))
const showNewBtn = computed(
  () => tableEditable.value && options.newBtn === true
)
const showCopyBtn = computed(
  () => tableEditable.value && options.copyBtn === true
)
const showDelBtn = computed(() => options.delBtn === true)
const hasActiveFilter = computed(() =>
  hasActiveFilterGroups(reactData.filterGroups)
)
const showColumnsPanel = computed(() => isSideBarEnabled(options))
const showAiAssistant = computed(() => isAiAssistantEnabled(options))
const customBtns = computed(() =>
  (options.btns ?? []).map((btn) => ({
    ...btn,
    icon: resolveIconComponent(btn.icon),
  }))
)

function runGlobalSearch() {
  reactData.currentPage = 1
  void $grid.queryByExamplePage()
}

const debouncedGlobalSearch = debounce(
  runGlobalSearch,
  GLOBAL_SEARCH_DEBOUNCE_MS
)

function handleGlobalSearchInput() {
  const trimmed = reactData.searchKeyword.trim()
  // 输入中过短：不触发请求，等够字数或清空后再搜
  if (trimmed.length > 0 && trimmed.length < GLOBAL_SEARCH_MIN_LENGTH) {
    debouncedGlobalSearch.cancel()
    return
  }
  debouncedGlobalSearch()
}

function handleGlobalSearch() {
  debouncedGlobalSearch.cancel()
  runGlobalSearch()
}

function handleMoreCommand(command: string) {
  switch (command) {
    case 'export':
      $grid.openExport?.()
      break
    case 'custom':
      $grid.openCustom?.()
      break
  }
}

onBeforeUnmount(() => {
  debouncedGlobalSearch.cancel()
})
</script>
